import type { OarTaskService } from '../../services/oar-task-service';
import type { OarStatementContentModel } from '../../models/oar-statement-content-model';
import type { ClaimAnalysisResult } from '../../types/claim-analysis';
import type { GenerateStatementContentCommand } from '../claim-analysis-command';

/**
 * 生成陈述书内容命令参数
 */
export interface GenerateStatementContentCommandParams {
    taskId: string;
    claimAnalysisResults: ClaimAnalysisResult[];
}

/**
 * 生成陈述书内容命令实现
 *
 * 执行流程：
 * 1. 调用 OarTaskService.generateStatementContent 生成陈述书
 * 2. 将结果更新到 OarStatementContentModel
 * 3. 返回陈述书内容
 */
export class GenerateStatementContentCommandImpl implements GenerateStatementContentCommand {
    private _service: OarTaskService;
    private _model: OarStatementContentModel;
    private _previousStatementContent?: string;
    private _previousGeneratedAt?: number;
    private _previousWordCount?: number;
    private _params: GenerateStatementContentCommandParams;

    constructor(
        service: OarTaskService,
        model: OarStatementContentModel,
        params: GenerateStatementContentCommandParams,
    ) {
        this._service = service;
        this._model = model;
        this._params = params;
    }

    /**
     * 检查命令是否可以执行
     *
     * @returns 是否可以执行
     */
    canExecute(): boolean {
        return !!this._params.taskId && Array.isArray(this._params.claimAnalysisResults) && this._params.claimAnalysisResults.length > 0;
    }

    /**
     * 执行生成陈述书内容命令
     *
     * @returns 陈述书内容字符串
     * @throws 当参数无效或生成失败时抛出错误
     */
    async execute(): Promise<string> {
        if (!this.canExecute()) {
            throw new Error('Invalid command parameters: taskId and non-empty claimAnalysisResults are required');
        }

        try {
            // 1. 保存当前数据用于撤销
            const currentData = this._model.modelData;

            this._previousStatementContent = currentData.statementContent;
            this._previousGeneratedAt = currentData.statementGeneratedAt;
            this._previousWordCount = currentData.statementWordCount;

            // 2. 调用服务生成陈述书内容
            const content = await this._service.generateStatementContent(
                this._params.taskId,
                this._params.claimAnalysisResults,
            );

            // 3. 更新 Model
            const now = Date.now();
            const newState = {
                ...currentData,
                taskId: this._params.taskId,
                statementContent: content,
                statementGeneratedAt: now,
                statementWordCount: content.length,
            };

            this._model.update(() => newState);

            return content;
        } catch (error) {

            // 如果执行失败，恢复之前的状态
            if (this._previousStatementContent !== undefined) {
                this._model.update(state => ({
                    ...state,
                    statementContent: this._previousStatementContent,
                    statementGeneratedAt: this._previousGeneratedAt,
                    statementWordCount: this._previousWordCount,
                }));
            }
            throw error;
        }
    }

    /**
     * 撤销命令，恢复到执行前的状态
     */
    async undo(): Promise<void> {
        if (this._previousStatementContent !== undefined) {
            this._model.update(state => ({
                ...state,
                statementContent: this._previousStatementContent,
                statementGeneratedAt: this._previousGeneratedAt,
                statementWordCount: this._previousWordCount,
            }));

            // 清理保存的数据
            this._previousStatementContent = undefined;
            this._previousGeneratedAt = undefined;
            this._previousWordCount = undefined;
        }
    }
}
